import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductList, getProductDetails, updateProductDetails, saveProductBasicInfo } from '../services/productService';
import { getCities, getManagers, getProductTypes, getContentMainList, getConsultList } from '../services/apiService';
import ProductSettingsModal from '../components/ProductSettingsModal';
import NewProductModal from '../components/NewProductModal';
import PriceStockModal from '../components/PriceStockModal';
import NewQuoteModal from '../components/NewQuoteModal';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { getUserInfo } from '../utils/tokenManager';
import { getMyBidStatusByCounsel } from '../services/openEstimateApi';

const QuoteManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  const [showForm, setShowForm] = useState(false);
  const [isNewProductOpen, setIsNewProductOpen] = useState(false);
  const [isNewQuoteOpen, setIsNewQuoteOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [consults, setConsults] = useState([]);
  const [selectedConsultNos, setSelectedConsultNos] = useState(() => new Set());
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [openHeaderFilterKey, setOpenHeaderFilterKey] = useState(null);
  const [headerFilterSearch, setHeaderFilterSearch] = useState({});
  const [headerFilterSelected, setHeaderFilterSelected] = useState({});
  const headerFilterRootRef = useRef(null);
  const headerFilterButtonRefs = useRef({});
  const headerFilterAnchorElRef = useRef(null);
  const [headerFilterAnchorRect, setHeaderFilterAnchorRect] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [contentMainList, setContentMainList] = useState(null);
  const [isPriceStockOpen, setIsPriceStockOpen] = useState(false);
  const [priceStockTarget, setPriceStockTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [consultMap, setConsultMap] = useState({}); // { [productNo:number]: 상담번호:number }
  const [bidMap, setBidMap] = useState({}); // { [상담번호:number]: { status, ... } } — Supabase 공개견적 응찰 상태
  
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    productType: '기타',
    productSubType: '',
    saleStatus: 'true',
  });

  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    cities: [],
    productTypes: [],
    productSubTypes: [],
  });
  
  const [formData, setFormData] = useState({
    productName: '',
    productType: '',
    city: '',
    country: '',
    duration: '',
    minParticipants: '',
    price: '',
    currency: 'KRW',
    description: '',
    itinerary: [{ time: '', activity: '', description: '' }],
    inventory: [{ date: '', available: '', price: '' }]
  });

  // Fetch product list when component mounts
  useEffect(() => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    const defaults = { startDate: toYmd(oneYearAgo), endDate: toYmd(today) };
    setDateRange(defaults);
    fetchProducts(defaults);
  }, []);

  // Load consult list once to map 팀컬러 -> 상담번호, then map to 상품번호
  useEffect(() => {
    const loadConsults = async () => {
      try {
        const list = await getConsultList({ startDate: dateRange.startDate, endDate: dateRange.endDate });
        // Build map: teamColor (numeric) -> consultNo
        const map = {};
        (Array.isArray(list) ? list : []).forEach((row) => {
          const teamColor = row?.팀컬러 != null ? Number(String(row.팀컬러).replace(/[^0-9]/g, '')) : null;
          const consultNo = row?.상담번호 != null ? Number(row.상담번호) : null;
          if (teamColor && consultNo) {
            map[teamColor] = consultNo;
          }
        });
        setConsultMap(map);
      } catch (e) {
        console.error('상담리스트 불러오기 실패:', e);
      }
    };
    loadConsults();
  }, [dateRange.startDate, dateRange.endDate]);

  // 공개 견적 응찰 상태(Supabase)를 상담번호 기준으로 로드. 판매사가 낙찰하면
  // estimate_bids.status 가 won/lost 로 바뀌므로, 상담관리 각 행에 join 해서
  // 낙찰/유찰 배지를 보여준다(기존 상담 SP 무수정).
  useEffect(() => {
    let cancelled = false;
    const loadBids = async () => {
      try {
        const map = await getMyBidStatusByCounsel();
        if (!cancelled) setBidMap(map || {});
      } catch (e) {
        console.error('응찰 상태 불러오기 실패:', e);
      }
    };
    loadBids();
    return () => { cancelled = true; };
  }, [consults]);

  // Populate filter options based on product list
  useEffect(() => {
    if (products.length > 0) {
      const countries = [...new Set(products.map(p => p.국가).filter(Boolean))];
      const productTypes = [...new Set(products.map(p => p.상품종류).filter(Boolean))];
      
      setFilterOptions(prev => ({ ...prev, countries, productTypes }));
    }
  }, [products]);

  // Update city and product sub-type options based on selection (product type fixed to '기타')
  useEffect(() => {
    if (filters.country) {
      const cities = [...new Set(products
        .filter(p => p.국가 === filters.country)
        .map(p => p.도시)
        .filter(Boolean))];
      setFilterOptions(prev => ({ ...prev, cities }));
    } else {
      setFilterOptions(prev => ({ ...prev, cities: [] }));
      if (filters.city !== '') setFilters(prev => ({...prev, city: ''}));
    }

    // Product type is fixed to '기타'
    const productSubTypes = [...new Set(products
      .filter(p => p.상품타입 === '기타')
      .map(p => p.상품타입)
      .filter(Boolean))];
    setFilterOptions(prev => ({ ...prev, productSubTypes }));
    if (!productSubTypes.includes(filters.productSubType)) {
      // reset if current sub-type no longer valid
      setFilters(prev => ({ ...prev, productSubType: '' }));
    }
  }, [filters.country, products]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Parse product creation datetime safely (expects formats like "YYYY-MM-DD HH:mm" or ISO)
  const getCreatedAt = (product) => {
    try {
      const raw = product?.생성일시 || product?.등록일시 || product?.createdAt || '';
      if (!raw) return 0;
      const str = String(raw).trim();
      // Normalize to ISO-like string for reliable parsing
      const iso = str.includes('T') ? str : (str.length === 16 ? str.replace(' ', 'T') + ':00' : str.replace(' ', 'T'));
      const t = Date.parse(iso);
      return isNaN(t) ? 0 : t;
    } catch {
      return 0;
    }
  };
  
  // Function to fetch products from API
  const fetchProducts = async ({ startDate, endDate } = {}) => {
    setLoading(true);
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        alert('로그인이 필요합니다. 다시 로그인해 주세요.');
        navigate('/login');
        return;
      }
      // Replace list source: 상담리스트 (DBO.TRP_S_%EC%83%81%EB%8B%B4%EB%A6%AC%EC%8A%A4%ED%8A%B8)
      const list = await getConsultList({ startDate, endDate });
      setConsults(Array.isArray(list) ? list : []);
      setError(null);
    } catch (err) {
      setError('목록을 불러오는데 실패했습니다.');
      console.error('Failed to fetch list:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleItineraryChange = (index, field, value) => {
    const newItinerary = [...formData.itinerary];
    newItinerary[index][field] = value;
    setFormData({
      ...formData,
      itinerary: newItinerary
    });
  };

  const addItineraryItem = () => {
    setFormData({
      ...formData,
      itinerary: [...formData.itinerary, { time: '', activity: '', description: '' }]
    });
  };

  const handleInventoryChange = (index, field, value) => {
    const newInventory = [...formData.inventory];
    newInventory[index][field] = value;
    setFormData({
      ...formData,
      inventory: newInventory
    });
  };

  const addInventoryItem = () => {
    setFormData({
      ...formData,
      inventory: [...formData.inventory, { date: '', available: '', price: '' }]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
alert('상품이 등록되었습니다.');
    setShowForm(false);
  };

  const quoteTableColumns = [
    { key: '견적명', label: '견적명', type: 'string' },
    { key: '인원수', label: '인원수', type: 'number' },
    { key: '상담상태', label: '상담상태', type: 'string' },
    { key: '응찰상태', label: '응찰상태', type: 'string' },
    { key: '판매업체', label: '판매업체', type: 'string' },
    { key: '등록일', label: '등록일', type: 'date' },
    { key: '출발일', label: '출발일', type: 'date' },
    { key: '담당자', label: '담당자', type: 'string' },
    { key: '요청자명', label: '요청자명', type: 'string' },
    { key: '요청자휴대폰', label: '요청자휴대폰', type: 'string' },
  ];

  const quoteTableColWidthClass = {
    견적명: 'w-[24rem]',
    인원수: 'w-[5.5rem]',
    상담상태: 'w-[7.5rem]',
    응찰상태: 'w-[7.5rem]',
    판매업체: 'w-[8rem]',
    등록일: 'w-[7rem]',
    출발일: 'w-[7rem]',
    담당자: 'w-[6.5rem]',
    요청자명: 'w-[8rem]',
    요청자휴대폰: 'w-[10rem]',
  };

  // 공개 견적 응찰 상태(Supabase estimate_bids.status)를 한글 라벨로.
  // 낙찰(won)/유찰(lost) 은 판매사가 낙찰 처리한 결과, 응찰중(bidding/submitted)은 미결.
  const 응찰상태_LABEL = {
    won: '낙찰',
    lost: '유찰',
    submitted: '응찰중',
    bidding: '응찰중',
  };
  const getBidStatusLabel = (consultNo) => {
    const cn = consultNo != null ? Number(consultNo) : null;
    if (!cn || !bidMap[cn]) return '';
    return 응찰상태_LABEL[bidMap[cn].status] || '';
  };

  const toggleSort = (key) => {
    // NOTE: Avoid calling setState inside another setState updater.
    // In React StrictMode, updaters can be evaluated more than once,
    // which can cause sortDir to toggle twice and look like it doesn't change.
    setSortDir((prevDir) => {
      if (sortKey !== key) return 'asc';
      return prevDir === 'asc' ? 'desc' : 'asc';
    });
    setSortKey(key);
  };

  useEffect(() => {
    if (!openHeaderFilterKey) return;
    const onMouseDown = (e) => {
      const pop = headerFilterRootRef.current;
      const anchor = headerFilterAnchorElRef.current;
      if (pop && pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      setOpenHeaderFilterKey(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openHeaderFilterKey]);

  useEffect(() => {
    if (!openHeaderFilterKey) return;
    const updateRect = () => {
      const el = headerFilterAnchorElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openHeaderFilterKey]);

  const columnValueOptions = useMemo(() => {
    const arr = Array.isArray(consults) ? consults : [];
    const map = {};
    quoteTableColumns.forEach((c) => {
      const set = new Set();
      arr.forEach((row) => {
        const v = String(getCellDisplay(row, c.key) ?? '').trim();
        if (v !== '') set.add(v);
      });
      map[c.key] = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'ko'));
    });
    return map;
  }, [consults, quoteTableColumns]);

  const toggleHeaderFilter = (key, anchorEl = null) => {
    setOpenHeaderFilterKey((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        headerFilterAnchorElRef.current = anchorEl;
        if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
          const r = anchorEl.getBoundingClientRect();
          setHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
        } else {
          setHeaderFilterAnchorRect(null);
        }
      } else {
        headerFilterAnchorElRef.current = null;
        setHeaderFilterAnchorRect(null);
      }
      return next;
    });
  };

  const onHeaderFilterSearchChange = (key, value) => {
    setHeaderFilterSearch((prev) => ({ ...prev, [key]: value }));
  };

  const isAllSelectedForKey = (key) => {
    const opts = columnValueOptions?.[key] || [];
    const selected = headerFilterSelected?.[key] || [];
    return opts.length > 0 && selected.length === opts.length;
  };

  const toggleAllForKey = (key) => {
    const opts = columnValueOptions?.[key] || [];
    setHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const next = current.length === opts.length ? [] : opts;
      return { ...prev, [key]: next };
    });
  };

  const toggleOptionForKey = (key, option) => {
    setHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const exists = current.includes(option);
      const next = exists ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  function normalizeDateToYmd(value) {
    if (!value) return '';
    const s = String(value).trim();
    let dateStr = s;
    if (dateStr.includes(' ')) dateStr = dateStr.split(' ')[0];
    if (dateStr.includes('T')) dateStr = dateStr.split('T')[0];
    return dateStr;
  }

  function firstNonEmpty(row, keys) {
    for (const k of keys) {
      const v = row?.[k];
      if (v != null && String(v).trim() !== '') return v;
    }
    return '';
  }

  function formatHeadcountDisplay(value) {
    if (value == null || value === '') return '';
    return String(value).replace(/\+1tc/gi, '').trim();
  }

  function getCellDisplay(row, key) {
    if (!row) return '';
    if (key === '견적명') return String(firstNonEmpty(row, ['견적명', '상담명', '상품명', '견적제목']) || '');
    if (key === '인원수') {
      const v = firstNonEmpty(row, ['인원수', '인원', '참가인원', '인원수합계']);
      return v != null ? formatHeadcountDisplay(v) : '';
    }
    if (key === '상담상태') return String(firstNonEmpty(row, ['상담상태', '상태', '상담상태명']) || '').trim();
    if (key === '응찰상태') return getBidStatusLabel(row?.상담번호);
    if (key === '판매업체') {
      const { companyNo } = getUserInfo() || {};
      const sellerNo = firstNonEmpty(row, ['판매업체번호', '판매업체No', '판매업체NO', '판매업체코드', '판매업체']);
      if (companyNo != null && sellerNo != null && String(companyNo) === String(sellerNo)) {
        return '직접등록';
      }
      const sellerName = String(firstNonEmpty(row, ['판매업체', '판매업체명', '판매업체이름', '판매처', '판매처명']) || '').trim();
      if (!sellerName && (sellerNo == null || String(sellerNo).trim() === '')) {
        return '직판';
      }
      return sellerName;
    }
    if (key === '등록일') return normalizeDateToYmd(firstNonEmpty(row, ['등록일', '등록일자', '등록일시', '생성일', '생성일시']));
    if (key === '출발일') return normalizeDateToYmd(firstNonEmpty(row, ['출발일', '출발일자', '여행출발일', '행사출발일']));
    if (key === '담당자') return String(firstNonEmpty(row, ['담당자', '상담담당자', '담당자명', '매니저', '매니저명']) || '');
    if (key === '요청자명') return String(firstNonEmpty(row, ['요청자명', '고객명', '신청자명', '문의자명', '예약자명']) || '');
    if (key === '요청자휴대폰') return String(firstNonEmpty(row, ['요청자휴대폰', '휴대폰', '휴대폰번호', '연락처', '전화번호']) || '');
    return String(row?.[key] ?? '');
  }

  const getCellSortValue = (row, key, type) => {
    if (!row) return null;
    if (type === 'number') {
      const n = Number(String(getCellDisplay(row, key)).replace(/,/g, ''));
      return Number.isFinite(n) ? n : null;
    }
    if (type === 'date') {
      const d = Date.parse(String(getCellDisplay(row, key) || ''));
      return Number.isFinite(d) ? d : null;
    }
    return (getCellDisplay(row, key) ?? '').toString();
  };

  // Filter consults based on search term and 상담상태
  const baseFilteredProducts = (Array.isArray(consults) ? consults : []).filter(row => {
    const search = (searchTerm || '').toLowerCase();
    if (search) {
      const fields = [
        getCellDisplay(row, '견적명'),
        getCellDisplay(row, '인원수'),
        getCellDisplay(row, '담당자'),
        getCellDisplay(row, '요청자명'),
        getCellDisplay(row, '요청자휴대폰'),
        getCellDisplay(row, '등록일'),
        getCellDisplay(row, '출발일'),
      ].map(v => String(v || '').toLowerCase());
      if (!fields.some(f => f.includes(search))) return false;
    }

    return true;
  });

  const headerFilteredProducts = (() => {
    const activeKeys = Object.keys(headerFilterSelected || {}).filter((k) => Array.isArray(headerFilterSelected?.[k]) && headerFilterSelected[k].length > 0);
    if (activeKeys.length === 0) return baseFilteredProducts;
    const selectedMap = Object.fromEntries(activeKeys.map((k) => [k, new Set((headerFilterSelected[k] || []).map((v) => String(v)))]));
    return baseFilteredProducts.filter((row) =>
      activeKeys.every((k) => {
        const display = String(getCellDisplay(row, k) ?? '').trim();
        return selectedMap[k].has(display);
      })
    );
  })();

  const finalProducts = (() => {
    const arr = headerFilteredProducts.slice();

    // Default behavior: keep existing registered-date desc sorting
    if (!sortKey) {
      arr.sort((a, b) => {
        const ad = Date.parse(String(getCellDisplay(a, '등록일') || '').replace(' ', 'T')) || 0;
        const bd = Date.parse(String(getCellDisplay(b, '등록일') || '').replace(' ', 'T')) || 0;
        return bd - ad;
      });
      return arr;
    }

    const meta = quoteTableColumns.find((c) => c.key === sortKey);
    const type = meta?.type || 'string';
    arr.sort((a, b) => {
      const av = getCellSortValue(a, sortKey, type);
      const bv = getCellSortValue(b, sortKey, type);
      if (av == null && bv == null) return 0;
      if (av == null) return -1;
      if (bv == null) return 1;
      let cmp = 0;
      if (type === 'number' || type === 'date') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), 'ko');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  })();

  // Helpers for display
  const getCategory = (p) => p?.카테고리 || p?.상품종류 || p?.상품타입 || '-';
  const getTravelPeriod = (p) => p?.기간 || p?.여행기간 || p?.소요시간 || '-';
  const getLastEventDate = (p) => p?.최종행사일 || p?.마지막행사일 || p?.행사종료일 || '';

  const formatDateTime = (value) => {
    if (!value) return '-';
    try {
      const date = typeof value === 'number' ? new Date(value) : new Date(String(value));
      if (Number.isNaN(date.getTime())) return '-';
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (e) {
      console.error('Failed to format datetime:', e);
      return '-';
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      const date = new Date(String(value));
      if (Number.isNaN(date.getTime())) return '-';
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    } catch (e) {
      console.error('Failed to format date:', e);
      return '-';
    }
  };

  // 견적관리 리스트에서 선택 시 주문내역 UI로 이동
  const openOrderListForProduct = (row) => {
    if (!row) return;
    const code = '-';
    const name = row.견적명 || '';
    const pn = row?.팀컬러 != null ? Number(String(row.팀컬러).replace(/[^0-9]/g, '')) : null;
    const consultNo = row?.상담번호 != null ? Number(row.상담번호) : (pn && consultMap[pn] ? consultMap[pn] : undefined);
    navigate(`/quote-detail/${encodeURIComponent(code)}`, {
      state: { productName: name, productNo: pn, consultNo }
    });
  };

  const selectedCount = selectedConsultNos.size;
  const selectedCountLabel = selectedCount > 0 ? `(${selectedCount})` : '';

  const toggleSelected = (consultNo, checked) => {
    const cn = consultNo != null ? Number(consultNo) : null;
    if (!cn) return;
    setSelectedConsultNos((prev) => {
      const next = new Set(prev);
      if (checked) next.add(cn);
      else next.delete(cn);
      return next;
    });
  };

  const visibleConsultNos = useMemo(() => {
    return finalProducts
      .map((row) => (row?.상담번호 != null ? Number(row.상담번호) : null))
      .filter((n) => Number.isFinite(n) && n > 0);
  }, [finalProducts]);

  const isAllVisibleSelected = useMemo(() => {
    if (!visibleConsultNos.length) return false;
    return visibleConsultNos.every((n) => selectedConsultNos.has(n));
  }, [visibleConsultNos, selectedConsultNos]);

  const toggleSelectAllVisible = (checked) => {
    setSelectedConsultNos((prev) => {
      const next = new Set(prev);
      visibleConsultNos.forEach((n) => {
        if (checked) next.add(n);
        else next.delete(n);
      });
      return next;
    });
  };

  const onOpenCompare = () => {
    const ids = Array.from(selectedConsultNos);
    if (ids.length < 2) {
      alert('비교할 상담(견적)을 2개 이상 선택해주세요.');
      return;
    }
    if (ids.length > 4) {
      alert('비교는 최대 4개까지 선택할 수 있습니다.');
      return;
    }
    navigate('/quotes/compare', { state: { consultNos: ids } });
  };

  const handleSettingsClick = async (product) => {
    setModalLoading(true);
    try {
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(product.상품번호, companyNo);
      if (details) {
        setSelectedProduct(details);
        setIsModalOpen(true);
      } else {
        setError('상품 상세 정보를 찾을 수 없습니다.');
      }
    } catch (err) {
      setError('상품 상세 정보를 불러오는데 실패했습니다.');
      console.error('Failed to fetch product details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // 가격/재고 모달 열기/닫기/저장
  const handleOpenPriceStock = (product) => {
    setPriceStockTarget(product);
    setIsPriceStockOpen(true);
  };
  const handleClosePriceStock = () => {
    setIsPriceStockOpen(false);
    setPriceStockTarget(null);
  };
  const handleSavePriceStock = async ({ productNo, data }) => {
    // TODO: API 연동 지점. data는 { 'YYYY-MM-DD': { price, stock } } 형태
    // 여기서는 스캐폴딩만: 저장 후 목록 리프레시 정도 처리
    try {
      // await savePriceStock({ productNo, entries: data })
      await fetchProducts();
    } catch (e) {
      console.error('가격/재고 저장 실패:', e);
    }
  };

  // 모달 닫힘 전 트리거에서 호출되어 콘텐츠 메인 리스트를 최신화
  const handlePreviewRefresh = async (productNo) => {
    try {
      if (!productNo) return;
      const refreshed = await getContentMainList({ productNo });
      setContentMainList(refreshed);
    } catch (e) {
      console.error('콘텐츠 메인 리스트 갱신 실패:', e);
    }
  };

  // 상품 카드에서 판매여부 토글 처리
  const handleToggleSale = async (product, nextSale) => {
    try {
      setLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { alert('로그인이 필요합니다.'); navigate('/login'); return; }
      const productNo = product.상품번호;

      // 최신 상세정보 조회 (기본정보용 베이스)
      const details = await getProductDetails(productNo, companyNo).catch(() => null);
      const original = details || {};

      // @기본정보 구성: 기존 값 유지 + 노출여부만 변경
      const basicInfo = {
        ...original,
        상품번호: productNo,
        노출여부: !!nextSale,
      };

      const keywordsStr = Array.isArray(original.키워드)
        ? original.키워드.join(',')
        : String(original.키워드 || '');

      await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordsStr });

      // 목록 새로고침으로 카드 상태 동기화
      await fetchProducts();
    } catch (err) {
      console.error('판매여부 변경 실패:', err);
      setError('판매여부 변경에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setModalLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { alert('로그인이 필요합니다.'); navigate('/login'); return false; }
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호;

      // 이름->코드 매핑 준비 (필요 시에만 호출)
      const [cities, managers, productTypes] = await Promise.all([
        getCities().catch(() => []),
        getManagers().catch(() => []),
        getProductTypes().catch(() => []),
      ]);

      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;

      // 원본 상세정보를 기반으로 @기본정보 구성
      const original = selectedProduct || {};

      // 키워드 처리: 입력 문자열을 "," 기준 split -> trim -> 빈값 제외
      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr
        ? keywordStr.split(',').map(k => k.trim()).filter(Boolean)
        : [];

      // 도시 매핑
      const cityName = updatedProduct.도시 ?? original.도시;
      const cityItem = findByName(cities, cityName);
      const cityNo = cityItem?.코드 ?? original.도시번호;

      // 상품타입 매핑
      const productTypeName = updatedProduct.상품타입 ?? original.상품타입;
      const productTypeItem = findByName(productTypes, productTypeName);
      const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;

      // 담당자 매핑 (상품담당 / 예약담당)
      const pdName = updatedProduct.상품담당 ?? original.상품담당;
      const rdName = updatedProduct.예약담당 ?? original.예약담당;
      const pdItem = findByName(managers, pdName);
      const rdItem = findByName(managers, rdName);
      const pdNo = pdItem?.코드 ?? original.상품담당번호;
      const rdNo = rdItem?.코드 ?? original.예약담당번호;

      // @기본정보: 원본 유지 + 수정 필드 반영 (미노출 필드는 원본 그대로 강제 보존)
      const basicInfo = {
        ...original,
        상품번호: productNo,
        상품명: updatedProduct.상품명 ?? original.상품명,
        도시번호: cityNo,
        도시: cityName,
        상품타입: productTypeName,
        상품타입코드: productTypeCode,
        상품코드: updatedProduct.상품코드 ?? original.상품코드, // 편집값 반영
        상품코드룰사용: original.상품코드룰사용 ?? false,
        상품담당: pdName,
        상품담당번호: pdNo,
        예약담당: rdName,
        예약담당번호: rdNo,
        디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당',
        소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '',
        소요: original.소요 ?? original.소요단위 ?? '',
        소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0,
        통화코드: original.통화코드 ?? '',
        통화: original.통화 ?? '',
        가이드타입: original.가이드타입 ?? '',
        가이드타입코드: original.가이드타입코드 ?? '',
        카테고리: original.카테고리 ?? '',
        카테고리코드: original.카테고리코드 ?? '',
        노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false),
        키워드: keywordArr,
        상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지,
      };

      // 저장 호출
      const result = await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordArr.join(',') });

      // 저장 후에는 조회 프로시저로 최신 상세 재조회 (키워드 포함)
      const refreshed = await getProductDetails(productNo, companyNo);
      if (refreshed) {
        setSelectedProduct(refreshed);
      }
      // 목록도 최신화
      await fetchProducts();
      alert('성공적으로 저장되었습니다.');

      return true;
    } catch (err) {
      setError('저장에 실패했습니다. 다시 시도해주세요.');
      console.error('Failed to save product details:', err);

      return false;
    } finally {
      setModalLoading(false);
    }
  };

  // no accordion/grouping

  return (
    <div className="space-y-4">
      {/* Modals */}
      {isModalOpen && (
        <ProductSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={modalLoading}
          onPreviewRefresh={handlePreviewRefresh}
        />
      )}
      {isPriceStockOpen && (
        <PriceStockModal
          product={priceStockTarget}
          open={isPriceStockOpen}
          onClose={handleClosePriceStock}
          onSave={handleSavePriceStock}
        />
      )}
      {isNewProductOpen && (
        <NewProductModal
          open={isNewProductOpen}
          onClose={() => setIsNewProductOpen(false)}
          onCreated={({ productNo }) => {
            setIsNewProductOpen(false);
            navigate(`/detail-settings/${productNo}`);
          }}
        />
      )}
      {isNewQuoteOpen && (
        <NewQuoteModal
          open={isNewQuoteOpen}
          onClose={() => setIsNewQuoteOpen(false)}
          onCreated={({ productNo, quoteResp }) => {
            setIsNewQuoteOpen(false);
            (async () => {
              try {
                const pn = Number(productNo);

                // 1) 우선 프로시저 응답(quoteResp)에서 상담번호를 직접 추출 시도
                let consultNo;
                try {
                  const row = quoteResp?.table?.[0] || quoteResp?.Table?.[0] || null;
                  if (row) {
                    const raw =
                      row.상담번호 ??
                      row.consultNo ??
                      row.ConsultNo ??
                      row.상담번호1 ??
                      row.Consult_No;
                    if (raw != null) {
                      const n = Number(raw);
                      if (Number.isFinite(n) && n > 0) consultNo = n;
                    }
                  }
                } catch (e) {
                  console.warn('신규 견적 응답에서 상담번호 추출 실패 (무시):', e);
                }

                // 2) 응답에서 상담번호를 얻지 못한 경우, ITN_S_상담리스트 재호출 후
                //    가장 첫 번째 행의 상담번호를 우선 사용
                if (consultNo == null) {
                  const list = await getConsultList();
                  const arr = Array.isArray(list) ? list : [];

                  // 2-1) 첫 번째 행의 상담번호 사용
                  const first = arr[0];
                  if (first?.상담번호 != null) {
                    const n = Number(first.상담번호);
                    if (Number.isFinite(n) && n > 0) consultNo = n;
                  }

                  // 2-2) 그래도 없으면, 기존처럼 팀컬러 = 상품번호 매핑으로 보조 검색
                  if (consultNo == null && arr.length > 0) {
                    const found = arr.find(r => {
                      const teamColor = r?.팀컬러 != null ? Number(String(r.팀컬러).replace(/[^0-9]/g, '')) : null;
                      return teamColor === pn;
                    });
                    if (found?.상담번호 != null) {
                      const n = Number(found.상담번호);
                      if (Number.isFinite(n) && n > 0) consultNo = n;
                    }
                  }
                }

                // 3) 상품 코드/이름 조회
                const { companyNo } = getUserInfo() || {};
                let productCode = '-';
                let productName = '';
                try {
                  const details = await getProductDetails(pn, companyNo).catch(() => null);
                  productCode = details?.상품코드 || details?.ProductCode || '-';
                  productName = details?.상품명 || details?.ProductName || '';
                } catch {}

                // 4) 상세 페이지로 이동 (consultNo는 없을 수도 있으므로 그대로 전달)
                navigate(`/quote-detail/${encodeURIComponent(productCode)}`, {
                  state: { productName, productNo: pn, consultNo, fromNewQuote: true }
                });

                // 5) 목록 리프레시는 백그라운드로
                fetchProducts();
              } catch (e) {
                console.error('신규 견적 상세 이동 실패:', e);
                // 실패 시에는 목록만 리프레시
                fetchProducts();
              }
            })();
          }}
        />
      )}

      {/* Filter and Search Bar */}
      <div className="mb-0">
        <div className="rounded-2xl bg-white p-5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:gap-4 items-end">

            {/* 검색기간 */}
            <div className="lg:col-span-5" data-tour="quote-period">
              <label className="block text-[13px] font-semibold text-gray-600">검색기간</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="block w-full h-9 px-2.5 text-sm border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 rounded-lg"
                />
                <span className="text-sm text-gray-400 whitespace-nowrap">~</span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="block w-full h-9 px-2.5 text-sm border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => fetchProducts({ startDate: dateRange.startDate, endDate: dateRange.endDate })}
                  className="inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm text-white font-semibold rounded-lg bg-primary-600 hover:bg-primary-700"
                >
                  적용
                </button>
              </div>
            </div>

            {/* 견적 검색 */}
            <div className="lg:col-span-4" data-tour="quote-search">
              
              <div className="mt-1 relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="inline-search-input"
                  className="block w-full h-9 pl-10 pr-3 border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm"
                  placeholder="견적 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* City Filter (shown only when country is selected) */}
            {filters.country && (
              <div className="lg:col-span-2">
                <label htmlFor="city-filter" className="block text-[13px] font-semibold text-gray-600">도시</label>
                <select id="city-filter" name="city" value={filters.city} onChange={handleFilterChange} className="mt-1 block w-full h-9 pl-2.5 pr-8 text-sm border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 rounded-lg">
                  <option value="">전체</option>
                  {filterOptions.cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}

            {/* (판매 여부) 필터는 숨김 처리 */}

            {/* Actions */}
            <div className={`lg:col-span-3 flex flex-col md:flex-row md:items-end md:flex-nowrap gap-2 md:justify-end`}>
              <button
                type="button"
                data-tour="quote-compare"
                onClick={onOpenCompare}
                disabled={selectedConsultNos.size < 2}
                className={`w-full md:w-auto inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm font-semibold rounded-lg md:flex-[0_0_auto] ${selectedConsultNos.size < 2 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50'}`}
              >
                비교견적서 {selectedCountLabel}
              </button>
              <button
                data-tour="quote-new"
                onClick={() => setIsNewQuoteOpen(true)}
                className="w-full md:w-auto inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm font-semibold rounded-lg text-white bg-primary-600 hover:bg-blue-500 focus:outline-none md:flex-[0_0_auto]"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                신규 견적 등록
              </button>
            </div>

            {/* Search moved to header */}
          </div>
          
        </div>
      </div>


      {loading && (
        <div className="flex justify-center items-center py-16 rounded-2xl bg-white">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      )}
      {error && (
        <div className="rounded-2xl bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div>
          <div className="rounded-2xl bg-white p-5">
           
            {openHeaderFilterKey && (() => {
              const key = openHeaderFilterKey;
              const widthPx = 224; // tailwind w-56
              const margin = 8;
              const rect = headerFilterAnchorRect;
              const leftBase = (rect?.left ?? 0) + (rect?.width ?? 0) / 2 - widthPx / 2;
              const topBase = (rect?.top ?? 0) + (rect?.height ?? 0) + 8;
              const maxLeft = Math.max(margin, (typeof window !== 'undefined' ? window.innerWidth : 0) - widthPx - margin);
              const left = Math.min(Math.max(margin, leftBase), maxLeft);

              return (
                <div
                  ref={headerFilterRootRef}
                  style={{ position: 'fixed', top: topBase, left, width: widthPx }}
                  className="bg-white border border-gray-200 rounded-md shadow-lg z-50"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      placeholder="검색..."
                      value={headerFilterSearch?.[key] ?? ''}
                      onChange={(e) => onHeaderFilterSearchChange(key, e.target.value)}
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    />
                  </div>
                  <div className="max-h-56 overflow-auto p-2 text-xs text-gray-700">
                    <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
                      <input type="checkbox" checked={isAllSelectedForKey(key)} onChange={() => toggleAllForKey(key)} />
                      <span>(모두 선택)</span>
                    </label>
                    <div className="my-1 border-t border-gray-100" />
                    {(columnValueOptions?.[key] || [])
                      .filter((v) => {
                        const q = String(headerFilterSearch?.[key] ?? '').trim().toLowerCase();
                        if (!q) return true;
                        return String(v).toLowerCase().includes(q);
                      })
                      .map((v) => (
                        <label key={String(v)} className="flex items-center gap-2 py-1 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={(headerFilterSelected?.[key] || []).includes(v)}
                            onChange={() => toggleOptionForKey(key, v)}
                          />
                          <span className="truncate">{String(v)}</span>
                        </label>
                      ))}
                  </div>
                </div>
              );
            })()}
            <div className="overflow-x-auto" data-tour="quote-table">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[3rem]" />
                  {quoteTableColumns.map((c) => (
                    <col key={c.key} className={quoteTableColWidthClass?.[c.key] || 'w-auto'} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="h-10 bg-gray-100 first:rounded-l-lg last:rounded-r-lg px-3 text-center text-[13px] font-medium text-gray-500 whitespace-nowrap"
                    >
                      <input
                        type="checkbox"
                        checked={isAllVisibleSelected}
                        onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    {quoteTableColumns.map((c) => (
                      <th
                        key={c.key}
                        scope="col"
                        data-tour={`quote-col-${c.key}`}
                        className="h-10 bg-gray-100 first:rounded-l-lg last:rounded-r-lg px-3 text-center text-[13px] font-medium text-gray-500 whitespace-nowrap"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span className="whitespace-nowrap">{c.label}</span>
                          <button
                            type="button"
                            className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-100 text-gray-500"
                            title="정렬"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSort(c.key);
                            }}
                          >
                            {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                          </button>
                          <button
                            type="button"
                            className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(headerFilterSelected?.[c.key]) && headerFilterSelected[c.key].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                            title="필터"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleHeaderFilter(c.key, e.currentTarget);
                            }}
                          >
                            <FunnelIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {finalProducts.length === 0 ? (
                    <tr>
                      <td colSpan={quoteTableColumns.length + 1} className="text-center py-16 text-sm text-gray-500">
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    finalProducts.map((row) => {
                        const 등록일 = (() => {
                          const raw = row?.등록일;
                          if (!raw) return '-';
                          try {
                            const d = new Date(String(raw).replace(' ', 'T'));
                            if (Number.isNaN(d.getTime())) return String(raw);
                            // 등록일은 연-월-일까지만 표시 (시간 제거)
                            return new Intl.DateTimeFormat('ko-KR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            }).format(d);
                          } catch { return String(raw); }
                        })();
                        const 출발일 = (() => {
                          const raw = row?.출발일;
                          if (!raw) return '-';
                          try {
                            const d = new Date(String(raw));
                            if (Number.isNaN(d.getTime())) return String(raw);
                            return new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
                          } catch { return String(raw); }
                        })();
                        return (
                          <tr
                            key={row.상담번호}
                            className="group cursor-pointer transition-colors border-b border-gray-200 hover:bg-gray-50 focus-within:bg-gray-50"
                            onClick={() => openOrderListForProduct(row)}
                            title="해당 상품의 주문내역 보기"
                          >
                            <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedConsultNos.has(Number(row.상담번호))}
                                onChange={(e) => toggleSelected(row.상담번호, e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-3 py-3 align-top text-sm text-gray-900 whitespace-nowrap truncate">{row.견적명 || '-'}</td>
                            <td className="text-center px-3 py-3 align-top text-sm text-gray-700 whitespace-nowrap truncate">{getCellDisplay(row, '인원수') || '-'}</td>
                            <td className="px-3 py-3 align-top text-center text-sm whitespace-nowrap">
                              {(() => {
                                const statusText = String(row.상담상태 || '').trim();
                                let colorClass = 'bg-gray-100 text-gray-700';
                                if (statusText === '상담요청') {
                                  colorClass = 'bg-blue-50 text-blue-700';
                                } else if (statusText === '진행중') {
                                  colorClass = 'bg-emerald-50 text-emerald-700';
                                } else if (statusText === '예약전환') {
                                  colorClass = 'bg-indigo-50 text-indigo-700';
                                } else if (statusText === '상담취소') {
                                  colorClass = 'bg-red-50 text-red-600';
                                }

                                return statusText ? (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
                                    {statusText}
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-400">-</span>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-3 align-top text-center text-sm whitespace-nowrap">
                              {(() => {
                                const bidText = getBidStatusLabel(row?.상담번호);
                                if (!bidText) return <span className="text-sm text-gray-400">-</span>;
                                let colorClass = 'bg-gray-100 text-gray-700';
                                if (bidText === '낙찰') {
                                  colorClass = 'bg-emerald-50 text-emerald-700';
                                } else if (bidText === '유찰') {
                                  colorClass = 'bg-red-50 text-red-600';
                                } else if (bidText === '응찰중') {
                                  colorClass = 'bg-amber-50 text-amber-700';
                                }
                                return (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
                                    {bidText}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{getCellDisplay(row, '판매업체') || '-'}</td>
                            <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{등록일}</td>
                            <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{출발일}</td>
                            <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{row.담당자 || '-'}</td>
                            <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{row.요청자명 || '-'}</td>
                            <td className="px-3 py-3 align-top text-center text-sm text-gray-700 whitespace-nowrap truncate">{row.요청자휴대폰 || '-'}</td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default QuoteManagement;
